import { Component } from '../../scripts/component';
import { fetchConfig, formatMoney } from '../../scripts/utils';
import { CartAddEvent } from './events';

/**
 * @typedef {object} SizeEntry
 * @property {string} size - The size option value (e.g. "42").
 * @property {number} number - Numeric form of the size, NaN when not parseable.
 * @property {string} image - Image URL for the card.
 * @property {string} title - Product title, used as image alt text.
 * @property {boolean} isPending - True for the size the customer is adding right now.
 * @property {string} removeKey - Cart line key to remove, empty for the pending entry.
 */

/**
 * Custom element that warns a customer who is about to add a second size of a
 * product they already have in the cart.
 *
 * The modal acts as a *gate* in front of add-to-cart: `product-form-component`
 * awaits `reviewAdd()` before it posts to the cart, so nothing is added until
 * the customer has made a choice. Closing the modal or choosing "continue with
 * both" lets the add proceed (and the cart drawer opens as usual); keeping the
 * size already in the cart cancels the add.
 *
 * Once a choice is made, the clicked control shows a loading state and the modal
 * stays open until the resulting cart operation finishes. It then closes in the
 * capture phase of the add's cart event — i.e. immediately before the cart
 * drawer's own (bubble-phase) handler opens the drawer.
 *
 * @typedef {object} DuplicateSizeModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} recommendation
 * @property {HTMLElement} recommendationText
 * @property {HTMLElement} cards
 * @property {HTMLElement} priceMessage
 * @property {HTMLButtonElement} continueButton
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLTemplateElement} cardTemplate
 *
 * @extends Component<DuplicateSizeModalRefs>
 */
class DuplicateSizeModal extends Component {
  requiredRefs = ['dialog', 'cards', 'priceMessage', 'cardTemplate'];

  /**
   * Localized string templates, loaded from the inline JSON.
   * @type {Record<string, string>}
   */
  #i18n = {};

  /** Saved scroll position */
  #savedScrollY = 0;

  /**
   * Resolver for the in-flight `reviewAdd()` promise.
   * @type {((proceed: boolean) => void) | null}
   */
  #resolveDecision = null;

  /** Guards against a second action while one is completing. */
  #pending = false;

  /** Fallback timer id used to close if the expected cart event never arrives. */
  #closeFallback = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#loadTranslations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#closeFallback) clearTimeout(this.#closeFallback);

    // Never leave an add-to-cart awaiting a decision that can no longer be made.
    // Resolve directly — the close flow can't run on a detached element, so the
    // promise would otherwise hang.
    const resolve = this.#resolveDecision;
    this.#resolveDecision = null;
    resolve?.(true);
  }

  /** Reads the inline localized strings so JS-built copy stays translatable. */
  #loadTranslations() {
    try {
      const script = this.querySelector('script[data-dsm-i18n]');
      if (script?.textContent) {
        const parsed = /** @type {Record<string, string>} */ (JSON.parse(script.textContent));
        // The Liquid `t` filter HTML-escapes its output (e.g. `<strong>` → `&lt;strong&gt;`,
        // `'` → `&#39;`), so decode the trusted, dev-authored strings back to markup here.
        for (const key of Object.keys(parsed)) {
          parsed[key] = this.#decodeEntities(parsed[key]);
        }
        this.#i18n = parsed;
      }
    } catch {
      this.#i18n = {};
    }
  }

  /**
   * Gate run by `product-form-component` before it posts an add-to-cart.
   *
   * @param {{ productId: string, variantId: string }} pending
   * @returns {Promise<boolean>} True to let the add proceed, false to cancel it.
   */
  async reviewAdd({ productId, variantId }) {
    if (!productId || !variantId) return true;
    if (this.refs.dialog.open) return true;

    const cart = await this.#fetchCart();
    const existing = cart?.items?.find(
      (item) => String(item.product_id) === String(productId) && String(item.variant_id) !== String(variantId)
    );

    // Nothing in the cart to clash with — let the add through untouched.
    if (!existing?.handle) return true;

    const product = await this.#fetchProduct(existing.handle);
    const pendingVariant = product?.variants?.find((variant) => String(variant.id) === String(variantId));
    if (!product || !pendingVariant) return true;

    const sizeData = await this.#fetchSizeData(existing.handle);

    /** @type {SizeEntry[]} */
    const entries = [
      {
        size: this.#extractSizeFromCartItem(existing),
        number: NaN,
        image: existing.featured_image?.url || existing.image || '',
        title: existing.product_title || product.title || '',
        isPending: false,
        removeKey: String(existing.key),
      },
      {
        size: this.#extractSizeFromVariant(product, pendingVariant),
        number: NaN,
        image: pendingVariant.featured_image?.src || product.featured_image || '',
        title: product.title || '',
        isPending: true,
        removeKey: '',
      },
    ];

    for (const entry of entries) entry.number = this.#parseSizeNumber(entry.size);

    // Show the smaller size on the left, matching the design.
    if (!Number.isNaN(entries[0].number) && !Number.isNaN(entries[1].number) && entries[1].number < entries[0].number) {
      entries.reverse();
    }

    return new Promise((resolve) => {
      this.#resolveDecision = resolve;
      this.#showModal(entries, pendingVariant.price, sizeData);
    });
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    try {
      const response = await fetch(this.dataset.cartUrl || '/cart.js');
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * @param {string} handle
   * @returns {Promise<any>}
   */
  async #fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Fetches the product's size_recommendation metaobject data via the Section
   * Rendering API. Product metafields aren't exposed on /products/{handle}.js,
   * so a dedicated section renders them as JSON in the product's context.
   * @param {string} handle
   * @returns {Promise<{ available: boolean, sizeRuns: string, sizeUnit: string } | null>}
   */
  async #fetchSizeData(handle) {
    try {
      const response = await fetch(`/products/${handle}?section_id=size-recommendation-data`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('script[data-size-recommendation]');
      if (!el?.textContent) return null;
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Renders and opens the modal for the two competing sizes.
   * @param {SizeEntry[]} entries - Ordered smaller size first.
   * @param {number} unitPriceCents
   * @param {{ available: boolean, sizeRuns: string, sizeUnit: string } | null} sizeData
   */
  #showModal(entries, unitPriceCents, sizeData) {
    const { dialog, cards, cardTemplate } = this.refs;

    this.#resetControls();

    const canRank = !Number.isNaN(entries[0].number) && !Number.isNaN(entries[1].number) && entries[0].number !== entries[1].number;
    const sizeRuns = sizeData?.available ? String(sizeData.sizeRuns || '').toLowerCase() : '';
    // Size unit (e.g. "EU"/"US") shown in front of the variant size on the card.
    const sizeUnit = String(sizeData?.sizeUnit || '').trim();
    const recommendedIndex = this.#resolveRecommendedIndex(sizeRuns, canRank);

    this.#renderRecommendation(sizeRuns, entries[0].size, entries[1].size, canRank);

    cards.innerHTML = '';

    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const fragment = /** @type {DocumentFragment} */ (cardTemplate.content.cloneNode(true));
      const card = /** @type {HTMLElement} */ (fragment.querySelector('.dsm__card'));

      const img = card.querySelector('img');
      if (img) {
        // A falsy src (`''`) is resolved as a relative URL by the browser, firing
        // a wasted request to the current page; use a transparent placeholder.
        img.src = entry.image
          ? `${entry.image.split('?')[0]}?width=300`
          : 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
        img.alt = entry.title;
      }

      const sizeLabel = card.querySelector('[data-size-label]');
      if (sizeLabel) sizeLabel.textContent = sizeUnit ? `${sizeUnit} ${entry.size}` : entry.size;

      if (i === recommendedIndex) {
        card.classList.add('dsm__card--recommended');
        card.querySelector('.dsm__badge')?.classList.remove('hidden');
      }

      const keepBtn = /** @type {HTMLElement | null} */ (card.querySelector('[data-keep-action]'));
      if (keepBtn) {
        keepBtn.textContent = this.#interpolate(this.#i18n.keepSize, { size: entry.size });
        keepBtn.dataset.keepChoice = entry.isPending ? 'pending' : 'existing';
        // Keeping the pending size means dropping the line already in the cart.
        keepBtn.dataset.removeKey = entry.isPending ? entries[1 - i].removeKey : '';
        keepBtn.setAttribute('on:click', '#duplicate-size-modal/keepSize');
      }

      cards.appendChild(fragment);
    }

    this.#renderPriceMessage(unitPriceCents);

    // Lock scroll and open
    this.#savedScrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#savedScrollY}px`;

    dialog.showModal();
    dialog.addEventListener('click', this.#onBackdropClick);
    dialog.addEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Maps how the model runs to which of the two entries to recommend.
   * Runs small → recommend the larger size; runs large → the smaller.
   * Standard (or unknown/unrankable) highlights neither card.
   * @param {string} sizeRuns
   * @param {boolean} canRank
   * @returns {number} 0 or 1 for a recommended card, -1 for none.
   */
  #resolveRecommendedIndex(sizeRuns, canRank) {
    if (!canRank) return -1;
    // Entries are ordered smaller size first.
    if (sizeRuns === 'small') return 1;
    if (sizeRuns === 'large') return 0;
    return -1;
  }

  /**
   * Populates (or hides) the size-recommendation callout.
   * @param {string} sizeRuns
   * @param {string} lowSize
   * @param {string} highSize
   * @param {boolean} canRank
   */
  #renderRecommendation(sizeRuns, lowSize, highSize, canRank) {
    const { recommendation, recommendationText } = this.refs;
    if (!recommendation || !recommendationText) return;

    let html = '';

    if (sizeRuns === 'standard') {
      html = this.#escapeHtml(this.#i18n.runsStandard || '');
    } else if (canRank && (sizeRuns === 'small' || sizeRuns === 'large')) {
      const template = sizeRuns === 'small' ? this.#i18n.runsSmall : this.#i18n.runsLarge;
      html = this.#interpolate(template, {
        low: this.#escapeHtml(lowSize),
        high: this.#escapeHtml(highSize),
      });
    }

    if (html) {
      recommendationText.innerHTML = html;
      recommendation.classList.remove('hidden');
    } else {
      recommendationText.textContent = '';
      recommendation.classList.add('hidden');
    }
  }

  /**
   * Renders the footer message. Markets with a return portal get the "save"
   * framing; markets without one are told returns are at their own expense.
   * @param {number} unitPriceCents
   */
  #renderPriceMessage(unitPriceCents) {
    const { priceMessage } = this.refs;

    if (this.dataset.returnPortal === 'true') {
      priceMessage.innerHTML = this.#interpolate(this.#i18n.saveMessage, {
        price: this.#escapeHtml(formatMoney(unitPriceCents)),
      });
    } else {
      priceMessage.textContent = this.#i18n.priceInternational || '';
    }
  }

  /**
   * Fills `{{ token }}` placeholders, leaving unknown ones untouched.
   * Values destined for innerHTML must already be escaped by the caller.
   * @param {string} template
   * @param {Record<string, string>} values
   * @returns {string}
   */
  #interpolate(template, values) {
    return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    );
  }

  /**
   * Handles "Keep size X". A spinner shows on the button until the cart is ready.
   * Keeping the pending size removes the line already in the cart and lets the
   * add proceed; keeping the existing size cancels the add and just opens the cart.
   * @param {Event} event
   */
  async keepSize(event) {
    if (this.#pending) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const btn = /** @type {HTMLElement | null} */ (target.closest('[data-keep-action]'));
    if (!btn) return;

    this.#pending = true;
    this.#trackEvent('duplicate_size_keep_one');
    this.#setLoading('keep', btn);

    if (btn.dataset.keepChoice === 'pending') {
      const removeKey = btn.dataset.removeKey;
      if (removeKey) await this.#removeFromCart(removeKey);
      // product-form adds the pending item; close once that lands (see #proceedAndClose).
      this.#proceedAndClose();
    } else {
      // Keep the size already in the cart: nothing is added, so close and show
      // the (unchanged) cart ourselves.
      this.#resolveGate(false);
      this.#closeModal();
      this.#openCartDrawer();
    }
  }

  /** "Continue with both" — the pending item is added alongside the existing one. */
  continueWithBoth() {
    if (this.#pending) return;

    this.#pending = true;
    this.#trackEvent('duplicate_size_continue_both');
    this.#setLoading('continue');
    this.#proceedAndClose();
  }

  /** Close (X, backdrop, Esc) — treated as "continue", so the add still happens. */
  close() {
    if (this.#pending) return;

    this.#pending = true;
    this.#trackEvent('duplicate_size_close');
    this.#setLoading('close');
    this.#proceedAndClose();
  }

  /**
   * Lets the gated add-to-cart proceed, keeping the modal open (and its loading
   * state visible) until the add completes. The modal then closes in the capture
   * phase of the add's cart event, immediately before the cart drawer opens.
   */
  #proceedAndClose() {
    const onAdded = () => {
      if (this.#closeFallback) {
        clearTimeout(this.#closeFallback);
        this.#closeFallback = 0;
      }
      document.removeEventListener(CartAddEvent.eventName, onAdded, true);
      this.#closeModal();
    };

    // Capture phase fires before the cart drawer's bubble-phase handler.
    document.addEventListener(CartAddEvent.eventName, onAdded, true);
    // Safety net: some failures (e.g. a network error) never dispatch the event.
    this.#closeFallback = window.setTimeout(onAdded, 8000);

    this.#resolveGate(true);
  }

  /**
   * Resolves the pending `reviewAdd()` promise exactly once.
   * @param {boolean} proceed
   */
  #resolveGate(proceed) {
    const resolve = this.#resolveDecision;
    this.#resolveDecision = null;
    resolve?.(proceed);
  }

  /** Opens the cart drawer directly (used when no add-to-cart event will fire). */
  #openCartDrawer() {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer'));
    if (drawer?.hasAttribute('auto-open') && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  /**
   * Marks the clicked control as loading and blocks further interaction.
   * @param {'continue' | 'close' | 'keep'} type
   * @param {HTMLElement} [button] - The keep button, when type is 'keep'.
   */
  #setLoading(type, button) {
    const { dialog, continueButton, closeButton } = this.refs;
    dialog.classList.add('dsm--busy');
    dialog.setAttribute('aria-busy', 'true');

    if (type === 'continue') continueButton?.classList.add('dsm__continue--loading');
    else if (type === 'close') closeButton?.classList.add('dsm__close--loading');
    else if (type === 'keep') button?.classList.add('dsm__keep-btn--loading');
  }

  /** Clears any loading/pending state so the modal reopens in a clean state. */
  #resetControls() {
    this.#pending = false;
    if (this.#closeFallback) {
      clearTimeout(this.#closeFallback);
      this.#closeFallback = 0;
    }

    const { dialog, continueButton, closeButton } = this.refs;
    dialog.classList.remove('dsm--busy');
    dialog.removeAttribute('aria-busy');
    continueButton?.classList.remove('dsm__continue--loading');
    closeButton?.classList.remove('dsm__close--loading');
    // Keep buttons live inside cards, which are rebuilt on every open.
  }

  /** Closes the modal immediately (synchronously) and restores page scroll. */
  #closeModal() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('click', this.#onBackdropClick);
    dialog.removeEventListener('keydown', this.#onKeyDown);

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#savedScrollY, behavior: 'instant' });

    dialog.close();
  }

  /**
   * Removes a cart line item by its key.
   * No cart:update is dispatched — the add-to-cart that follows re-renders the
   * cart sections, so emitting one here would only cause a flash of stale UI.
   * @param {string} key
   */
  async #removeFromCart(key) {
    const body = JSON.stringify({ updates: { [key]: 0 } });

    try {
      await fetch(`${Theme.routes.cart_update_url}.js`, fetchConfig('json', { body }));
    } catch (error) {
      console.error('[duplicate-size-modal] Failed to remove item:', error);
    }
  }

  /**
   * Extracts the size value from a Shopify cart line item.
   * @param {any} item
   * @returns {string}
   */
  #extractSizeFromCartItem(item) {
    if (Array.isArray(item.options_with_values)) {
      const sizeOption = item.options_with_values.find((option) => this.#isSizeOptionName(option?.name));
      if (sizeOption) return String(sizeOption.value);
      if (item.options_with_values.length) return String(item.options_with_values[0].value);
    }

    return item.variant_title ? String(item.variant_title) : '';
  }

  /**
   * Extracts the size value from a product.js variant.
   * @param {any} product
   * @param {any} variant
   * @returns {string}
   */
  #extractSizeFromVariant(product, variant) {
    const names = (product.options || []).map((option) => (typeof option === 'string' ? option : option?.name) || '');
    const values = variant.options || [];
    const index = names.findIndex((name) => this.#isSizeOptionName(name));

    if (index >= 0 && values[index] != null) return String(values[index]);
    if (values.length) return String(values[0]);

    return variant.title ? String(variant.title) : '';
  }

  /**
   * @param {string} name
   * @returns {boolean}
   */
  #isSizeOptionName(name) {
    const normalized = String(name || '').toLowerCase().trim();
    return normalized === 'size' || normalized === 'størrelse' || normalized === 'str' || normalized === 'str.';
  }

  /**
   * Parses a size string to a comparable number, handling the mixed fractions
   * some brands use (e.g. "41 1/3" → 41.333, "40 2/3" → 40.667) alongside plain
   * and decimal sizes ("41", "42.5", "42,5" → 42.5).
   * @param {string} size
   * @returns {number} NaN when no number is present.
   */
  #parseSizeNumber(size) {
    const normalized = String(size)
      .trim()
      .toLowerCase()
      .replace(',', '.')
      // Normalise the common unicode vulgar fractions to "n/n".
      .replace('⅓', ' 1/3')
      .replace('⅔', ' 2/3')
      .replace('½', ' 1/2')
      .replace('¼', ' 1/4')
      .replace('¾', ' 3/4');

    // Whole number + fraction, e.g. "41 1/3".
    const mixed = normalized.match(/(\d+)\s+(\d+)\s*\/\s*(\d+)/);
    if (mixed) {
      const [, whole, numerator, denominator] = mixed;
      if (Number(denominator)) return Number(whole) + Number(numerator) / Number(denominator);
    }

    // Bare fraction, e.g. "1/3".
    const fraction = normalized.match(/^(\d+)\s*\/\s*(\d+)/);
    if (fraction) {
      const [, numerator, denominator] = fraction;
      if (Number(denominator)) return Number(numerator) / Number(denominator);
    }

    // Plain or decimal number, e.g. "42.5".
    const decimal = normalized.match(/\d+(\.\d+)?/);
    return decimal ? parseFloat(decimal[0]) : NaN;
  }

  /**
   * Escapes a string for safe insertion via innerHTML.
   * @param {string} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }

  /**
   * Decodes HTML entities in a trusted string (from the localized JSON).
   * @param {string} value
   * @returns {string}
   */
  #decodeEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value ?? '');
    return textarea.value;
  }

  /**
   * Fires analytics events.
   * @param {string} action
   */
  #trackEvent(action) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', action);
      }
      document.dispatchEvent(new CustomEvent('duplicate-size-modal:action', { detail: { action } }));
    } catch {
      // Non-critical
    }
  }

  /** @param {MouseEvent} event */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };
}

if (!customElements.get('duplicate-size-modal')) {
  customElements.define('duplicate-size-modal', DuplicateSizeModal);
}
